import { ThemeEvents } from '@theme/events';

const APPAREL_CONFIG = JSON.parse(document.getElementById('np-apparel-config')?.textContent || 'null');
const PRODUCT_FORM_SELECTOR = APPAREL_CONFIG ? 'product-form-component[data-product-id="' + APPAREL_CONFIG.productId + '"]' : '[data-np-apparel-unconfigured]';
const PREVIEW_SELECTOR = '.vm--modal[role="dialog"]';
const PREVIEW_ATC_SELECTOR = '#preview-teeAtcButton';
const PREVIEW_QUANTITY_TEMPLATE_ID = 'np-apparel-preview-quantity-template';
const AUTOMATIC_PRINT_INK = Object.freeze(APPAREL_CONFIG?.inks ?? {});
const DARK_INK_BACKGROUNDS = new Set(APPAREL_CONFIG?.darkInkBackgrounds ?? []);

/** The main product selector remains the only quantity submitted to Shopify. */
function getMainQuantity(productForm) {
  const input = productForm.querySelector('quantity-selector-component input[name="quantity"]');
  if (!(input instanceof HTMLInputElement) || input.closest(PRODUCT_FORM_SELECTOR) !== productForm) return null;
  const component = input.closest('quantity-selector-component');
  if (!component || !['setValue', 'updateButtonStates', 'onQuantityChange', 'getCurrentValues', 'getEffectiveMax']
    .every((method) => typeof component[method] === 'function')) return null;
  return { component, input };
}

class ApparelPreviewQuantity {
  session = null;
  pageListeners = null;

  start() {
    if (this.pageListeners && !this.pageListeners.signal.aborted) return;
    this.pageListeners = new AbortController();
    document.addEventListener('click', this.onPreviewClick, {
      capture: true,
      signal: this.pageListeners.signal,
    });
  }

  stop = () => {
    this.closeSession();
    this.pageListeners?.abort();
  };

  onPreviewClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button.tee-btn--preview');
    const productForm = button?.closest(PRODUCT_FORM_SELECTOR);
    if (!productForm) return;
    this.openSession(productForm);
  };

  openSession(productForm) {
    this.closeSession();
    const session = {
      productForm,
      modal: null,
      controls: null,
      input: null,
      syncing: false,
      frame: null,
      waitTimer: null,
      waitObserver: null,
      modalObserver: null,
      statusObserver: null,
      removalObserver: null,
      containerRemovalObserver: null,
      formObserver: null,
      listeners: new AbortController(),
    };
    this.session = session;

    const schedule = () => this.schedule(session);
    session.waitObserver = new MutationObserver(schedule);
    // Temporary discovery only: stop on mount, failed validation, close or navigation.
    session.waitObserver.observe(document.documentElement, { childList: true, subtree: true });
    session.waitTimer = setTimeout(() => {
      if (this.session === session && !session.modal) this.closeSession();
    }, 10000);
    this.schedule(session);
  }

  schedule(session) {
    if (this.session !== session || session.frame !== null) return;
    session.frame = requestAnimationFrame(() => {
      session.frame = null;
      if (this.session !== session) return;
      this.refresh(session);
    });
  }

  refresh(session) {
    if (!session.productForm.isConnected) return this.closeSession();
    if (!session.modal) {
      const modal = document.querySelector(PREVIEW_ATC_SELECTOR)?.closest(PREVIEW_SELECTOR);
      if (!(modal instanceof HTMLElement)) return;
      this.mountModal(session, modal);
    }
    const { modal } = session;
    if (!modal.isConnected || modal.getAttribute('aria-expanded') === 'false') return this.closeSession();
    const main = getMainQuantity(session.productForm);
    if (!main) { session.controls?.remove(); return; }
    const atc = modal.querySelector(PREVIEW_ATC_SELECTOR);
    const footer = atc?.closest('.tee-dcontent-footer');
    if (!footer) return;
    if (!session.controls || !session.input) {
      const template = document.getElementById(PREVIEW_QUANTITY_TEMPLATE_ID);
      if (!(template instanceof HTMLTemplateElement)) return this.closeSession();
      const controls = template.content.firstElementChild?.cloneNode(true);
      if (!(controls instanceof HTMLElement)) return this.closeSession();
      const input = controls.querySelector('[data-np-apparel-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return this.closeSession();
      session.controls = controls;
      session.input = input;
      const options = { signal: session.listeners.signal };
      controls.addEventListener('click', (event) => this.onQuantityButton(session, event), options);
      input.addEventListener('input', () => {
        if (input.value && input.validity.valid) this.commit(session, input.value);
      }, options);
      input.addEventListener('change', () => this.commit(session, input.value), options);
      input.addEventListener('blur', () => this.commit(session, input.value), options);
    }
    // Vue can replace a footer. Move our existing controls only when necessary.
    if (!footer.contains(session.controls)) footer.prepend(session.controls);
    this.syncFromMain(session, main);
  }

  mountModal(session, modal) {
    session.modal = modal;
    session.waitObserver?.disconnect();
    session.waitObserver = null;
    clearTimeout(session.waitTimer);
    session.waitTimer = null;
    const schedule = () => this.schedule(session);
    const options = { signal: session.listeners.signal };
    session.formObserver = new MutationObserver(schedule);
    session.formObserver.observe(session.productForm, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['min', 'max', 'step', 'disabled', 'data-cart-quantity'],
    });
    session.productForm.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      const main = getMainQuantity(session.productForm);
      if (!session.syncing && event.target === main?.component) schedule();
    }, options);
    for (const name of ['input', 'change']) {
      session.productForm.addEventListener(name, (event) => {
        if (!session.syncing && event.target === getMainQuantity(session.productForm)?.input) schedule();
      }, options);
    }

    const container = modal.parentElement;
    session.modalObserver = new MutationObserver(schedule);
    session.modalObserver.observe(modal, { childList: true, subtree: true });
    session.statusObserver = new MutationObserver(schedule);
    session.statusObserver.observe(modal, { attributes: true, attributeFilter: ['aria-expanded'] });
    // Outside the modal itself, watch direct-child removal only, never body subtrees.
    session.removalObserver = new MutationObserver(schedule);
    session.removalObserver.observe(container ?? modal, { childList: true });
    if (container?.parentNode) {
      session.containerRemovalObserver = new MutationObserver(schedule);
      session.containerRemovalObserver.observe(container.parentNode, { childList: true });
    }
    modal.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button');
      if (button?.matches(PREVIEW_ATC_SELECTOR)) {
        // Runs before Teeinblue's handler, including a manually typed value not yet blurred.
        this.commit(session, session.input?.value);
        this.submitFromPreview(session, event);
      } else if (event.target.closest('.tee-dialog-close') ||
        button?.parentElement?.matches('.tee-dcontent-footer')) {
        this.closeSession();
      }
    }, { ...options, capture: true });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.schedule(session);
    }, options);
  }

  submitFromPreview(session, event) {
    const submit = session.productForm.querySelector('button[type="submit"][name="add"]');
    const close = session.modal?.querySelector('.tee-dcontent-footer > button.tee-btn:not(.tee-btn--atc)');
    if (!(submit instanceof HTMLButtonElement) || submit.disabled ||
      !(close instanceof HTMLButtonElement)) return;
    // Horizon's preview action closes the dialog without reaching the product
    // submission. Use its working native button so Teeinblue still validates,
    // saves the design and supplies all cart properties itself. Suppress the
    // original footer action to prevent a second submission.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (session.submitting) return;
    session.submitting = true;
    close.click();
    submit.click();
  }

  syncFromMain(session, main = getMainQuantity(session.productForm)) {
    if (!main || !session.input || !session.controls) return;
    const { min, step, value } = main.component.getCurrentValues();
    const max = main.component.getEffectiveMax();
    session.input.min = String(min);
    session.input.step = String(step);
    if (max === null) session.input.removeAttribute('max');
    else session.input.max = String(max);
    if (document.activeElement !== session.input || session.syncing) session.input.value = String(value);
    session.input.disabled = main.input.disabled;
    const minus = session.controls.querySelector('[data-np-apparel-quantity-minus]');
    const plus = session.controls.querySelector('[data-np-apparel-quantity-plus]');
    if (minus instanceof HTMLButtonElement) minus.disabled = main.input.disabled || value <= min;
    if (plus instanceof HTMLButtonElement) plus.disabled = main.input.disabled || (max !== null && value >= max);
  }

  commit(session, rawValue) {
    if (this.session !== session || session.syncing) return;
    const main = getMainQuantity(session.productForm);
    if (!main || main.input.disabled) return;
    const { min, step, value } = main.component.getCurrentValues();
    const max = main.component.getEffectiveMax();
    const entered = Number.parseInt(String(rawValue), 10);
    const bounded = Math.min(max ?? Infinity, Math.max(min, Number.isFinite(entered) ? entered : min));
    const normalized = min + Math.floor((bounded - min) / step) * step;
    session.syncing = true;
    try {
      if (normalized !== value) {
        main.component.setValue(String(normalized));
        main.component.updateButtonStates();
        main.component.onQuantityChange();
        // Teeinblue reads the native product quantity; keep its input listeners informed.
        main.input.dispatchEvent(new Event('input', { bubbles: true }));
        main.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncFromMain(session, main);
    } finally {
      session.syncing = false;
    }
  }

  onQuantityButton(session, event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-np-apparel-quantity-minus], [data-np-apparel-quantity-plus]');
    const main = getMainQuantity(session.productForm);
    if (!(button instanceof HTMLButtonElement) || button.disabled || !main) return;
    event.preventDefault();
    const { step, value } = main.component.getCurrentValues();
    const direction = button.hasAttribute('data-np-apparel-quantity-plus') ? 1 : -1;
    this.commit(session, value + step * direction);
  }

  closeSession() {
    const session = this.session;
    if (!session) return;
    this.session = null;
    session.listeners.abort();
    for (const name of ['waitObserver', 'modalObserver', 'statusObserver', 'removalObserver', 'containerRemovalObserver', 'formObserver']) {
      session[name]?.disconnect();
    }
    if (session.frame !== null) cancelAnimationFrame(session.frame);
    if (session.waitTimer !== null) clearTimeout(session.waitTimer);
    session.controls?.remove();
  }
}

/**
 * Teeinblue's saved title default does not initialize its native required picker.
 * Initialize the confirmed blank picker through its own label, once per field.
 */
class ApparelFormDefaults {
  productForm = null;
  formObserver = null;
  formListeners = null;
  pageListeners = null;
  waitObserver = null;
  waitTimer = null;
  waitExpired = false;
  frame = null;
  attemptedTitles = new Set();
  attemptedLegacyMigration = false;
  migrationProtected = false;
  nativeActivation = false;
  fieldCache = new Map();
  pendingTemplate = null;
  restorePasses = 0;

  start() {
    if (this.pageListeners && !this.pageListeners.signal.aborted) return;
    if (!document.getElementById(PREVIEW_QUANTITY_TEMPLATE_ID)) return;
    this.pageListeners = new AbortController();
    this.waitExpired = false;
    const options = { signal: this.pageListeners.signal };
    document.addEventListener('shopify:section:load', () => {
      this.waitExpired = false;
      this.findProductForm();
    }, options);
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target instanceof Element && this.productForm && event.target.contains(this.productForm)) {
        this.unmountForm();
      }
    }, options);
    this.findProductForm();
  }

  findProductForm() {
    const productForm = Array.from(document.querySelectorAll(PRODUCT_FORM_SELECTOR))
      .find((form) => !form.closest(PREVIEW_SELECTOR));
    if (productForm instanceof HTMLElement) {
      this.stopWaiting();
      if (this.productForm !== productForm) this.mountForm(productForm);
      this.schedule();
      return;
    }
    if (this.waitObserver || this.waitExpired) return;
    this.waitObserver = new MutationObserver(() => this.schedule());
    this.waitObserver.observe(document.documentElement, { childList: true, subtree: true });
    this.waitTimer = setTimeout(() => {
      this.waitExpired = true;
      this.stopWaiting();
    }, 10000);
  }

  mountForm(productForm) {
    this.unmountForm();
    this.productForm = productForm;
    this.formListeners = new AbortController();
    this.formObserver = new MutationObserver(() => this.schedule());
    this.formObserver.observe(productForm, { childList: true, subtree: true });
    const options = { signal: this.formListeners.signal };
    productForm.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.isTrusted && !this.nativeActivation) {
        const picker = event.target.closest('#tee-field--' + APPAREL_CONFIG.artworkId + '-template');
        const nextTemplate = picker && event.target.closest('.tee-radio')?.querySelector('input')?.value;
        if (nextTemplate && APPAREL_CONFIG.templates[nextTemplate]) {
          this.captureTemplateFields(productForm.querySelector('#tee-artwork-form'));
          this.pendingTemplate = nextTemplate;
          this.restorePasses = 0;
        }
        if (event.target.closest('#tee-artwork-form') &&
          event.target.closest('label, input, button, select, textarea')) this.migrationProtected = true;
        const field = event.target.closest('.tee-field--additional_option');
        if (field && Object.values(APPAREL_CONFIG.templates).some(layout => field.id === 'tee-field--' + layout.titleId) &&
          event.target.closest('.tee-radio')) this.attemptedTitles.add(field.id);
      }
      this.schedule();
    }, { ...options, capture: true });
    for (const name of ['input', 'change']) {
      productForm.addEventListener(name, (event) => {
        if (event.isTrusted && !this.nativeActivation && event.target instanceof Element &&
          event.target.closest('#tee-artwork-form')) {
          this.migrationProtected = true;
        }
        this.schedule();
      }, options);
    }
  }

  schedule() {
    if (this.frame !== null || this.pageListeners?.signal.aborted) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.productForm?.isConnected) {
        this.unmountForm();
        if (this.pageListeners && !this.pageListeners.signal.aborted) this.findProductForm();
        return;
      }
      this.refreshForm();
    });
  }

  refreshForm() {
    const form = this.productForm?.querySelector('#tee-artwork-form');
    if (!(form instanceof HTMLElement) || form.closest(PREVIEW_SELECTOR)) return;
    this.reconcileNativeRadios(form);
    const selected = form.querySelector('#tee-field--' + APPAREL_CONFIG.artworkId + '-template .tee-radio.active input');
    const layout = selected ? APPAREL_CONFIG.templates[selected.value] : Object.values(APPAREL_CONFIG.templates)[0];
    if (!(layout && typeof layout === 'object')) return;
    if (selected && this.migrateLegacy(form, selected)) return;
    if (selected) this.restoreTemplateFields(form, selected.value, layout);
    if (layout.backgroundId) this.syncPrintInk(form, layout.backgroundId);
    const title = layout.titleId ? form.querySelector('#tee-field--' + layout.titleId) : null;
    if (title instanceof HTMLElement && !this.attemptedTitles.has(title.id) &&
      !title.querySelector('.tee-radio.active, .tee-clipart.active, input:checked')) {
      const input = Array.from(title.querySelectorAll('input')).find(e => String(e.value) === String(layout.defaultTitleOption));
      const label = input && Array.from(title.querySelectorAll('label[for]')).find(e => e.htmlFor === input.id);
      if (label instanceof HTMLLabelElement) {
        this.attemptedTitles.add(title.id);
        this.clickNativeLabel(label);
      }
    }
    const nameIds = new Set(layout.nameIds ?? []);
    for (const input of form.querySelectorAll('input[type="text"][name]')) {
      const placeholder = input.name === layout.customTitleId ? 'Ex: Papa' : nameIds.has(input.name) ? 'Ex: Sophia' : null;
      if (placeholder && input.getAttribute('placeholder') !== placeholder) input.setAttribute('placeholder', placeholder);
    }
  }

  captureTemplateFields(form) {
    if (!form) return;
    const selected = form.querySelector('#tee-field--' + APPAREL_CONFIG.artworkId + '-template .tee-radio.active input');
    const layout = APPAREL_CONFIG.templates[selected?.value] ?? Object.values(APPAREL_CONFIG.templates)[0];
    for (const [name, id] of Object.entries(layout?.fields ?? {})) {
      const field = form.querySelector('#tee-field--' + id);
      if (!field || getComputedStyle(field).display === 'none') continue;
      const text = field.querySelector('input[type="text"], textarea');
      const choice = field.querySelector('.tee-radio.active input, .tee-clipart.active input, input:checked');
      if (text instanceof HTMLInputElement || text instanceof HTMLTextAreaElement) this.fieldCache.set(name, {kind:'text', value:text.value});
      else if (choice instanceof HTMLInputElement) this.fieldCache.set(name, {kind:'choice', value:choice.value});
    }
  }

  restoreTemplateFields(form, templateId, layout) {
    if (this.pendingTemplate !== templateId) return;
    for (const [name,id] of Object.entries(layout.fields ?? {})) {
      const saved = this.fieldCache.get(name);
      const field = saved && form.querySelector('#tee-field--' + id);
      if (!field) continue;
      if (saved.kind === 'text') {
        const input = field.querySelector('input[type="text"], textarea');
        if ((input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) && input.value !== saved.value) {
          input.value = saved.value;
          input.dispatchEvent(new Event('input', {bubbles:true}));
          input.dispatchEvent(new Event('change', {bubbles:true}));
        }
      } else {
        const current = field.querySelector('.tee-radio.active input, .tee-clipart.active input, input:checked');
        if (current?.value === saved.value) continue;
        const input = Array.from(field.querySelectorAll('input[type="radio"]')).find(e => e.value === saved.value);
        const label = input && Array.from(field.querySelectorAll('label[for]')).find(e => e.htmlFor === input.id);
        if (label instanceof HTMLLabelElement) this.clickNativeLabel(label);
      }
    }
    // The second pass covers fields revealed by a restored conditional choice.
    if (++this.restorePasses < 3) this.schedule();
    else this.pendingTemplate = null;
  }

  /** Browser form restoration can overwrite checked without changing Vue's choice. */
  reconcileNativeRadios(form) {
    for (const group of form.querySelectorAll('[role="radiogroup"]')) {
      const selected = group.querySelector('.tee-radio.active input, .tee-clipart.active input');
      if (!(selected instanceof HTMLInputElement)) continue;
      // The native active option owns the artwork and saved design. Repair its
      // DOM presentation without dispatching a change or rewriting plugin data.
      for (const input of group.querySelectorAll('input[type="radio"]')) {
        input.checked = input === selected;
      }
    }
  }

  /** Commit ink through Teeinblue's native field so saved artwork matches preview. */
  syncPrintInk(form, backgroundId) {
    const inkId = AUTOMATIC_PRINT_INK[backgroundId];
    if (!inkId) return;
    const background = form.querySelector(`#tee-field--${backgroundId}`);
    const ink = form.querySelector(`#tee-field--${inkId}`);
    if (!(background instanceof HTMLElement) || !(ink instanceof HTMLElement)) return;
    if (ink.getAttribute('aria-hidden') !== 'true') ink.setAttribute('aria-hidden', 'true');
    ink.hidden = true;
    ink.style.setProperty('display', 'none', 'important');
    const selected = background.querySelector('.tee-clipart.active input, input:checked');
    if (!(selected instanceof HTMLInputElement)) return;
    const desired = DARK_INK_BACKGROUNDS.has(selected.value.toLowerCase()) ? '#161616' : '#ffffff';
    const current = ink.querySelector('.tee-clipart.active input, input:checked');
    if (current instanceof HTMLInputElement && current.value.toLowerCase() === desired) return;
    const option = Array.from(ink.querySelectorAll('input[type="radio"]'))
      .find((input) => input instanceof HTMLInputElement && input.value.toLowerCase() === desired);
    if (!(option instanceof HTMLInputElement)) return;
    const label = Array.from(ink.querySelectorAll('label[for]'))
      .find((item) => item.getAttribute('for') === option.id);
    if (label instanceof HTMLElement) this.clickNativeLabel(label);
  }

  migrateLegacy(form, selected) {
    const legacy = APPAREL_CONFIG.legacy;
    if (!legacy || selected.value !== legacy.templateId || this.attemptedLegacyMigration || this.migrationProtected) return false;
    const oldCount = form.querySelector('#tee-field--' + legacy.countField + ' .tee-radio.active input');
    const count = oldCount instanceof HTMLInputElement ? Number(oldCount.value) - legacy.countOffset : 1;
    const templateId = legacy.countTemplates[count] ?? legacy.countTemplates[1];
    const replacement = form.querySelector('#tee-field--' + APPAREL_CONFIG.artworkId + '-template .tee-radio input[value="' + templateId + '"]');
    const label = replacement?.closest('.tee-radio')?.querySelector('label[for]');
    if (!(replacement instanceof HTMLInputElement) || !(label instanceof HTMLElement) || label.getAttribute('for') !== replacement.id) return false;
    this.attemptedLegacyMigration = true;
    this.clickNativeLabel(label);
    this.schedule();
    return true;
  }

  clickNativeLabel(label) {
    this.nativeActivation = true;
    try {
      label.click();
    } finally {
      this.nativeActivation = false;
    }
  }

  stopWaiting() {
    this.waitObserver?.disconnect();
    this.waitObserver = null;
    if (this.waitTimer !== null) clearTimeout(this.waitTimer);
    this.waitTimer = null;
  }

  unmountForm() {
    this.formObserver?.disconnect();
    this.formObserver = null;
    this.formListeners?.abort();
    this.formListeners = null;
    this.productForm = null;
  }

  stop = () => {
    this.stopWaiting();
    this.unmountForm();
    this.pageListeners?.abort();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  };
}

const apparelPreviewQuantity = new ApparelPreviewQuantity();
const apparelFormDefaults = new ApparelFormDefaults();
apparelPreviewQuantity.start();
apparelFormDefaults.start();
window.addEventListener('pagehide', apparelPreviewQuantity.stop);
window.addEventListener('pagehide', apparelFormDefaults.stop);
window.addEventListener('pageshow', () => {
  apparelPreviewQuantity.start();
  apparelFormDefaults.start();
});
